/**
 * Purpose:
 *   Sidebar navigation menu component with responsive behavior.
 *   Fixed sidebar on desktop, drawer overlay on mobile.
 *   Supports collapsible mode (icons only) on desktop.
 *
 * Notes:
 *   - Desktop: Fixed sidebar on the left side with collapse toggle
 *   - Mobile: Slide-in drawer with dark overlay
 *   - Supports expandable menu sections with smooth animations
 *   - Uses useMenu hook for state management
 *   - Uses useMenuEffects hook for side effects (scroll lock, keyboard, notifications)
 *   - Delegates menu items rendering to MenuItems component
 *   - Menu items configuration from project constants; labels localized with common namespace
 */

'use client';

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@/util/functions/cx';
import { MENU_ITEMS } from '@/util/constants/menu';
import { useMenu } from './(hooks)/useMenu';
import { useMenuEffects } from './(hooks)/useMenuEffects';
import { MenuItems } from './MenuItems';
import { MenuMobileDrawer } from './MenuMobileDrawer';
import { Icon } from '@/components/ui/icon/Icon';
import {
  filterMenuItemsByPermissions,
  localizeMenuItems,
} from './(utils)/localizeMenuItems';
import { useSessionAccount } from '@/lib/auth/useSessionAccount';
import {
  MENU_WRAPPER_DESKTOP,
  MENU_WRAPPER_EXPANDED,
  MENU_WRAPPER_COLLAPSED,
  MENU_CONTAINER_DESKTOP,
  MENU_TOGGLE_BUTTON,
  MENU_TOGGLE_ICON,
  MENU_TOGGLE_ICON_COLLAPSED,
  MENU_TOGGLE_ICON_EXPANDED,
} from './Menu.styles';

export interface MenuProps {
  /** Whether the mobile menu is open */
  isOpen?: boolean;
  /** Whether the desktop sidebar is collapsed */
  isCollapsed?: boolean;
  /** Callback when mobile menu should close */
  onClose?: () => void;
  /** Callback when desktop collapsed state should toggle */
  onToggleCollapsed?: () => void;
}

// Animation duration in ms
const ANIMATION_DURATION = 350;

// Smooth width transition
const WRAPPER_TRANSITION = `width ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`;

export const Menu: React.FC<MenuProps> = ({
  isOpen = false,
  isCollapsed = false,
  onClose,
  onToggleCollapsed,
}) => {
  const { t } = useTranslation('common');
  const { permissions } = useSessionAccount();
  const navigationLabel = t('menu.main_navigation');
  const menuItems = useMemo(
    () => filterMenuItemsByPermissions(localizeMenuItems(MENU_ITEMS, t), permissions),
    [permissions, t]
  );
  const {
    expandedItems,
    toggleExpanded,
    isActive,
    isParentActive,
    handleNavigate,
  } = useMenu({ items: menuItems });

  // Handle side effects (body scroll, keyboard, parent notifications)
  useMenuEffects({
    isOpen,
    onClose,
  });

  const toggleIconClasses = cx(
    MENU_TOGGLE_ICON,
    isCollapsed ? MENU_TOGGLE_ICON_COLLAPSED : MENU_TOGGLE_ICON_EXPANDED
  );

  const wrapperClasses = cx(
    MENU_WRAPPER_DESKTOP,
    isCollapsed ? MENU_WRAPPER_COLLAPSED : MENU_WRAPPER_EXPANDED
  );

  return (
    <>
      {/* Desktop sidebar wrapper - contains menu + toggle button */}
      <div
        className={wrapperClasses}
        style={{ transition: WRAPPER_TRANSITION }}
      >
        {/* Menu content with scroll */}
        <aside
          className={MENU_CONTAINER_DESKTOP}
          aria-label={navigationLabel}
        >
          <MenuItems
            items={menuItems}
            ariaLabel={navigationLabel}
            expandedItems={expandedItems}
            isActive={isActive}
            isParentActive={isParentActive}
            onToggle={toggleExpanded}
            onNavigate={handleNavigate}
            isCollapsed={isCollapsed}
          />
        </aside>

        {/* Collapse toggle button - positioned on the edge */}
        <button
          type="button"
          onClick={onToggleCollapsed}
          className={MENU_TOGGLE_BUTTON}
          aria-label={isCollapsed ? t('menu.expand') : t('menu.collapse')}
          aria-expanded={!isCollapsed}
        >
          <Icon
            src="/icons/chevron-down.svg"
            size={14}
            className={toggleIconClasses}
          />
        </button>
      </div>

      <MenuMobileDrawer
        isOpen={isOpen}
        items={menuItems}
        expandedItems={expandedItems}
        isActive={isActive}
        isParentActive={isParentActive}
        onToggle={toggleExpanded}
        onNavigate={handleNavigate}
        onClose={onClose}
        navigationLabel={navigationLabel}
        closeLabel={t('menu.close')}
      />
    </>
  );
};
